/**
 * The parts of the recorder that are not the browser.
 *
 * `useAudioRecorder` is a hook and needs a `MediaRecorder`, a `MediaStream` and
 * a real microphone, none of which exist in a test runner. The two things in it
 * that have actually gone wrong in production — container selection and chunk
 * ordering — are pulled out here so they can be tested without any of that.
 *
 * Both are inherited from Craft Together, and both are scar tissue:
 *
 *   - a hardcoded `audio/webm;codecs=opus` recorded nothing at all on Safari,
 *     so a pair with one Safari participant produced one-sided audio that was
 *     only discovered after the session;
 *   - `event.data.arrayBuffer().then(send)` can resolve out of order for two
 *     chunks fired back to back, which `MediaRecorder` does routinely on
 *     startup. The server used to concatenate in arrival order, so the WebM
 *     header landed mid-container and roughly half the recordings would not
 *     open. Our server sorts by `seq` as well, but a chain that preserves order
 *     on the way out is what keeps the two agreeing.
 */

/** Preference order. First supported wins; `undefined` means browser default. */
export const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/mpeg',
  'audio/ogg;codecs=opus',
] as const

/**
 * `isSupported` is injected so this is a pure function of what the browser
 * claims. Old browsers throw from `isTypeSupported`; a throw is a no.
 */
export function pickRecorderMimeType(
  isSupported: (mime: string) => boolean,
  candidates: readonly string[] = MIME_CANDIDATES,
): string | undefined {
  for (const candidate of candidates) {
    try {
      if (isSupported(candidate)) return candidate
    } catch {
      // Some old browsers throw rather than returning false. Skip.
    }
  }
  return undefined
}

export interface ChunkSink {
  (payload: { chunk: ArrayBuffer; seq: number; mimeType?: string; sessionId?: string; recordingId?: string }): void
}

/**
 * Stamp every chunk with the round it belongs to.
 *
 * The tail of a recording is sent *after* `ROUND_ENDED`: the final
 * `ondataavailable` only fires on `recorder.stop()`, which the client calls once
 * it has been told the round is over, and the blob then goes through an
 * `await blob.arrayBuffer()` on top of that. For the server to accept those
 * bytes it has to hold the finished recording open for a moment past the
 * whistle — and the moment it does, the room can no longer answer "which
 * recording", because the room may already have been reset for another go.
 *
 * So the client answers. `session` is read at send time rather than captured,
 * because the sink is created before the round it will record; what it must
 * return is the round that was *being recorded*, which is why the caller holds
 * it in a ref rather than reading the room.
 */
export function withSession(send: ChunkSink, session: () => string | null): ChunkSink {
  return (payload) => {
    const sessionId = session()
    send(sessionId ? { ...payload, sessionId } : payload)
  }
}

export interface ChunkChain {
  /**
   * Queue one blob. The seq is assigned here, synchronously, in the order the
   * blobs arrived — so it describes the recording's true order even if the
   * conversions finish in a different one.
   */
  push(blob: { arrayBuffer(): Promise<ArrayBuffer> }): void
  /** Resolves once every queued chunk has been handed to the sink. */
  drain(): Promise<void>
  /** What `audioDone` should report: how many, and the last seq used. */
  accounting(): { chunkCount: number; lastSeq: number }
  reset(): void
}

export function createChunkChain(send: ChunkSink, mimeType?: string): ChunkChain {
  let tail: Promise<void> = Promise.resolve()
  let seq = 0
  let sent = 0

  return {
    push(blob) {
      const mine = seq
      seq += 1
      tail = tail.then(async () => {
        try {
          send({ chunk: await blob.arrayBuffer(), seq: mine, mimeType })
          sent += 1
        } catch (err) {
          // A chunk that cannot be converted is a gap the server will flag.
          // Dropping it is better than breaking the chain for every chunk after.
          console.warn('[audio] chunk conversion failed:', err)
        }
      })
    },
    drain() {
      return tail.catch(() => undefined)
    },
    accounting() {
      return { chunkCount: sent, lastSeq: seq - 1 }
    },
    reset() {
      tail = Promise.resolve()
      seq = 0
      sent = 0
    },
  }
}

// ---------------------------------------------------------------------------
// Recorder runs
// ---------------------------------------------------------------------------

/**
 * An id for one recorder run, from start to stop.
 *
 * A page reloaded mid-round starts a new `MediaRecorder` whose seq restarts at
 * 0 and whose first chunk is a fresh container header. Without a name the
 * server could only splice it onto the first run's file — a second header
 * mid-file, which no decoder plays past — or drop it as duplicate seqs, which
 * is what used to happen. With one, each run is its own file.
 */
export function newRecordingId(random: () => number = Math.random): string {
  let id = ''
  for (let i = 0; i < 16; i += 1) id += Math.floor(random() * 36).toString(36)
  return id
}

// ---------------------------------------------------------------------------
// Delivery: keep every chunk until the server says it has it
// ---------------------------------------------------------------------------

export interface ChunkAck {
  ok: boolean
  retry?: boolean
}

export interface ChunkPayload {
  chunk: ArrayBuffer
  seq: number
  mimeType?: string
  sessionId?: string
  recordingId?: string
}

export interface AckedSenderOptions {
  /** Put one chunk on the wire; call `ack` with the server's answer. */
  emit(payload: ChunkPayload, ack: (result: ChunkAck) => void): void
  now?: () => number
  /** How long an unanswered chunk waits before it is sent again. */
  retryAfterMs?: number
  /** At most this many resends per `resendDue` call — the server allows ~10/s. */
  maxResendsPerPass?: number
  /** At most this many chunks are kept waiting; the oldest go first. */
  maxPending?: number
  /** A chunk still unacknowledged this long after it was first sent is given up. */
  giveUpAfterMs?: number
}

export interface AckedSender {
  send(payload: ChunkPayload): void
  /**
   * Send again whatever has waited too long for an answer, oldest first and
   * paced. Called on a timer and after a reconnect.
   */
  resendDue(): number
  /** Chunks sent and not yet acknowledged. */
  pending(): number
  /** Chunks the sender gave up on (over `maxPending`), for the console. */
  dropped(): number
}

/**
 * The delivery half of the recorder.
 *
 * Socket.IO does not guarantee that an emit reaches the server. A chunk emitted
 * into a connection that has died but not yet noticed is lost; a chunk emitted
 * while disconnected is buffered and flushed on reconnect — *before* the
 * page's own `rejoin` — so the server, which knows no seat for the new socket
 * yet, drops it. A blip mid-round therefore cost a recording every second it
 * lasted. So every chunk is kept until the server acknowledges it, a refusal
 * that is about *now* (`retry`) keeps it, and anything unanswered is sent again
 * after a pause. The server treats an identical resend as a no-op.
 */
export function createAckedSender(options: AckedSenderOptions): AckedSender {
  const now = options.now ?? Date.now
  const retryAfterMs = options.retryAfterMs ?? 3000
  const maxResends = options.maxResendsPerPass ?? 4
  const maxPending = options.maxPending ?? 400
  const giveUpAfterMs = options.giveUpAfterMs ?? 120_000
  const waiting = new Map<string, { payload: ChunkPayload; sentAt: number; firstSentAt: number }>()
  let dropped = 0

  const keyOf = (payload: ChunkPayload): string => `${payload.recordingId ?? ''}:${payload.seq}`

  const put = (payload: ChunkPayload): void => {
    const key = keyOf(payload)
    const firstSentAt = waiting.get(key)?.firstSentAt ?? now()
    const entry = { payload, sentAt: now(), firstSentAt }
    waiting.set(key, entry)
    options.emit(payload, (result) => {
      // A stale answer — the chunk was resent since — still settles it: any
      // `ok` means the server holds these bytes.
      if (!waiting.has(key)) return
      if (result && result.ok) waiting.delete(key)
      else if (result && !result.retry) waiting.delete(key)
      else {
        // "Not now": try again after the pause rather than immediately.
        const held = waiting.get(key)
        if (held) held.sentAt = now()
      }
    })
  }

  return {
    send(payload) {
      put(payload)
      while (waiting.size > maxPending) {
        const oldest = waiting.keys().next().value as string
        waiting.delete(oldest)
        dropped += 1
      }
    },
    resendDue() {
      for (const [key, entry] of waiting) {
        if (now() - entry.firstSentAt >= giveUpAfterMs) {
          waiting.delete(key)
          dropped += 1
        }
      }
      const due = [...waiting.values()]
        .filter((entry) => now() - entry.sentAt >= retryAfterMs)
        .sort((a, b) => a.sentAt - b.sentAt || a.payload.seq - b.payload.seq)
        .slice(0, maxResends)
      for (const entry of due) put(entry.payload)
      return due.length
    },
    pending() {
      return waiting.size
    },
    dropped() {
      return dropped
    },
  }
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * What the recorder should do now. Pure, so the rule is testable without a
 * browser: record while the round is playing and there is something to record
 * from; stop, and report, once it is not.
 */
export function recorderStep(state: {
  phase: string
  haveStream: boolean
  recording: boolean
}): 'start' | 'stop' | 'none' {
  if (state.phase === 'playing' && state.haveStream && !state.recording) return 'start'
  if (state.phase !== 'playing' && state.recording) return 'stop'
  return 'none'
}

/**
 * Whether a page needs to ask for the microphone again.
 *
 * A reload mid-round reclaims the seat and skips every gate — the mic check
 * included — which used to leave the page with no microphone at all: no
 * recording for the rest of the round, and no voice either. A page that is
 * playing, in a room with a partner, without a microphone, asks for it again;
 * the browser remembers the permission, so this is normally silent.
 */
export function needsMicrophone(state: {
  stage: string
  seats: number | null | undefined
  haveMic: boolean
}): boolean {
  if (state.stage !== 'playing' || state.haveMic) return false
  // Still asking the room how many seats it has: wait, rather than prompt a
  // solo player for a microphone the room will never use.
  if (state.seats === undefined) return false
  return state.seats !== 1
}
